import * as yup from "yup";

import { getPlayerRoleIssues } from "@ds/modules/experiences/utils/player-roles";
import { createDeviceValuesValidator } from "@ds/modules/experiences/utils/validation-schema";

import { type Shape } from "@ds/utils/validation";

import { getNewPlayerRoleFieldPath, NEW_PLAYER_ID, withNewPlayer } from "./new-player-role";

const nameSchema = yup.string().required("Please specify player name");
const experienceSchema = yup.object().nullable().shape<Shape<Experience>>({});

export const createValidationSchema = {
  step1: yup.object().shape<Shape<PlayerFormData>>({
    player: yup.object().shape<Shape<Player>>({
      name: nameSchema,
    }),
  }),
  step2: yup.object().shape<Shape<PlayerFormData>>({
    experience: experienceSchema,
  }),
};

/**
 * The Role page of the add player wizard: the new player's values checked against their fields' rules, and the
 * errors the server would reject the player for (a role already full, a `unique` value another player has). A
 * missing role or required value is left as a warning, same as on the experience's players card.
 */
export const getCreateRoleValidationSchema = (
  roles: FormationRoleMap,
  properties: TemplateFormationFieldPropertyMap,
  experience?: Experience,
) => {
  const validateDeviceValues = createDeviceValuesValidator(roles, properties);

  return yup.object().test("playerRole", function validatePlayerRole(value) {
    const formation = (value as PlayerFormData | undefined)?.formation;
    const issues = getPlayerRoleIssues({ roles, properties, ...withNewPlayer(experience, formation) });

    const errors = [
      ...validateDeviceValues(formation).map(({ path, message }) => ({ path: `formation.${path}`, message })),
      ...(issues[NEW_PLAYER_ID] || [])
        .filter(({ severity }) => severity === "error")
        .map(({ field, message }) => ({ path: getNewPlayerRoleFieldPath(field), message })),
    ].map(error => this.createError(error));

    return errors.length ? new yup.ValidationError(errors) : true;
  });
};

export const updateValidationSchema = {
  info: yup.object().shape<Shape<Player>>({
    name: nameSchema,
  }),
  experience: yup.object().shape<Shape<PlayerFormData>>({
    experience: experienceSchema,
  }),
};
